import { useCallback, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { CaseStudy } from "@shared/schema";
import { BlurFade } from "@/components/ui/blur-fade";

const expoOut = [0.77, 0, 0.175, 1] as const;

/** Known display name overrides for slugified client names */
const CLIENT_NAME_OVERRIDES: Record<string, string> = {
  "amc-networks": "AMC Networks",
  "forbes-2": "Forbes",
  "kkr": "KKR",
  "canvasprints-com": "CanvasPrints.com",
  "lifepro-fitness": "LifePro Fitness",
  "mywallst": "MyWallSt",
  "versa-products": "VersaDesk",
  "jacquie-aiche": "Jacquie Aiche",
};

/** Convert slug-format name to proper display name */
function displayName(study: CaseStudy): string {
  const raw = study.clientName || study.client || "";
  if (CLIENT_NAME_OVERRIDES[raw]) return CLIENT_NAME_OVERRIDES[raw];
  // Title-case: "grown-brilliance" → "Grown Brilliance"
  return raw
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getImage(study: CaseStudy) {
  return (
    study.imageUrl ||
    study.featuredImage ||
    `/images/case-studies/${study.slug}.avif`
  );
}

/**
 * Responsive picture for case study card images. Generates AVIF + WebP
 * sources from the optimize-images pipeline's standard variant suffixes
 * (-480, -800, -1200, -1600). Falls back to a plain <img> if the URL
 * doesn't match a recognized image extension (e.g. external CDN).
 */
const CASE_STUDY_WIDTHS = [480, 800, 1200, 1600] as const;
const CASE_STUDY_SIZES = "(max-width: 1023px) 100vw, 675px";

function CaseStudyImg({ url, alt }: { url: string; alt: string }) {
  const m = url.match(/^(.+)\.(png|jpe?g|webp|avif)$/i);
  if (!m) {
    return (
      <img
        src={url}
        alt={alt}
        width={675}
        height={810}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    );
  }
  const base = m[1];
  const avifSet =
    CASE_STUDY_WIDTHS.map((w) => `${base}-${w}.avif ${w}w`).join(", ") +
    `, ${base}.avif`;
  const webpSet =
    CASE_STUDY_WIDTHS.map((w) => `${base}-${w}.webp ${w}w`).join(", ") +
    `, ${base}.webp`;
  return (
    <picture>
      <source type="image/avif" srcSet={avifSet} sizes={CASE_STUDY_SIZES} />
      <source type="image/webp" srcSet={webpSet} sizes={CASE_STUDY_SIZES} />
      <img
        src={url}
        alt={alt}
        width={675}
        height={810}
        className="w-full h-full object-cover"
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
}

/**
 * Single case study card:
 * - Tall image (3:4) with white client logo centered
 * - Clip-path reveal from left on scroll
 * - On hover: two KPIs fade up at bottom of image
 * - Below image: bordered bar with italic client name (left) + "View More" outline button (right)
 */
function CaseStudyCard({
  study,
  index,
}: {
  study: CaseStudy;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { amount: 0.05, once: true });

  const stats = [
    study.keyValue1Number ? { number: study.keyValue1Number, label: study.keyValue1Text } : null,
    study.keyValue2Number ? { number: study.keyValue2Number, label: study.keyValue2Text } : null,
  ].filter(Boolean) as Array<{ number: string; label: string | null }>;

  return (
    <div ref={ref} className="w-[calc(100vw-48px)] sm:w-[calc(100vw-80px)] lg:w-[675px]" style={{ flexShrink: 0 }}>
      <Link href={`/case-studies/${study.slug}`}>
        <div className="group cursor-pointer">
          {/* Image container — bg-cream/5 so off-screen cards are visible against charcoal */}
          <div className="relative bg-cream/5" style={{ aspectRatio: "15/16", border: "1px solid var(--border-structural-dark)" }}>
            {/* Clip-path reveal on the image only — container stays visible */}
            <motion.div
              initial={{ clipPath: "inset(0% 100% 0% 0%)" }}
              animate={
                isInView
                  ? { clipPath: "inset(0% 0% 0% 0%)" }
                  : { clipPath: "inset(0% 100% 0% 0%)" }
              }
              transition={{
                clipPath: { duration: 1.1, delay: index * 0.12, ease: expoOut },
              }}
              className="absolute inset-0 overflow-hidden"
            >
              <CaseStudyImg url={getImage(study)} alt={study.title} />
            </motion.div>

            {/* Dark overlay for logo/KPI readability */}
            <div className="absolute inset-0 bg-charcoal/30 pointer-events-none" />

            {/* White client logo — centered */}
            {study.clientLogo && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <img
                  src={study.clientLogo}
                  alt={displayName(study)}
                  width={200}
                  height={64}
                  loading="lazy"
                  className="h-12 md:h-16 w-auto max-w-[55%] object-contain brightness-0 invert opacity-80"
                />
              </div>
            )}

            {/* Hover KPIs — fade up at bottom of image */}
            {stats.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 p-6 flex gap-8 opacity-100 translate-y-0 lg:opacity-0 lg:translate-y-3 lg:group-hover:opacity-100 lg:group-hover:translate-y-0 transition-all duration-500 pointer-events-none z-10">
                {stats.map((stat, i) => (
                  <div key={i}>
                    <span className="type-h3 text-cream block">{stat.number}</span>
                    <span className="type-label text-cream/60">{stat.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info bar — client name + excerpt (left), arrow button (right) */}
          <div
            className="flex mt-4"
            style={{ border: "1px solid var(--border-structural-dark)" }}
          >
            <div className="flex-1 min-w-0 px-5 py-5">
              <h3 className="type-h3 text-cream italic uppercase mb-2">
                {displayName(study)}
              </h3>
              {study.excerpt && (
                <p className="type-body text-cream/60 line-clamp-2">
                  {study.excerpt}
                </p>
              )}
            </div>
            <div
              className="shrink-0 w-16 flex items-center justify-center border-l pointer-events-none transition-all duration-300 group-hover:bg-indigo"
              style={{ borderColor: "var(--border-structural-dark)" }}
            >
              <ArrowUpRight className="w-5 h-5 text-cream/60 group-hover:text-cream transition-colors duration-300" />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

export function CaseStudyGallery({ className = "" }: { className?: string }) {
  const { data: allStudies } = useQuery<CaseStudy[]>({
    queryKey: ["/api/case-studies"],
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: false,
    dragFree: true,
  });

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  // Filter to homepage-featured or super-featured, fallback to first 6 published
  const featured = (allStudies || [])
    .filter((s) => s.published && (s.isHomepageFeatured || s.isSuperFeatured))
    .sort((a, b) => (a.caseStudyOrder ?? 999) - (b.caseStudyOrder ?? 999));

  const studies = featured.length >= 3
    ? featured.slice(0, 8)
    : (allStudies || []).filter((s) => s.published).slice(0, 6);

  if (studies.length === 0) return null;

  return (
    <section id="case-studies" className={className} style={{ backgroundColor: "var(--charcoal)" }}>
      {/* Header */}
      <div className="container-content section-padding-y" style={{ paddingBottom: "clamp(32px, 4vw, 56px)" }}>
        <BlurFade inView delay={0}>
          <span className="type-eyebrow" style={{ color: "var(--terra-lt)" }}>
            Case Studies
          </span>
          <h2 className="type-h1" style={{ color: "var(--cream)", marginTop: "var(--space-gap-xs)", maxWidth: "var(--max-w-heading)" }}>
            The proof is in the performance.
          </h2>
          <p className="type-body" style={{ color: "var(--text-muted-on-dark)", marginTop: "var(--space-gap-sm)", maxWidth: "var(--max-w-text)" }}>
            Performance. Grit. Intelligence. And a simple, rock-solid commitment to client profit.
          </p>
        </BlurFade>

        {/* Navigation arrows */}
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: "var(--space-gap-md)" }}>
          <button
            onClick={scrollPrev}
            aria-label="Previous"
            className="opacity-50 hover:opacity-100 transition-opacity"
            style={{ display: "flex", background: "none", border: "none", cursor: "pointer", padding: "8px 0", minHeight: 44 }}
          >
            <svg className="w-12 h-7 lg:w-20 lg:h-7" viewBox="0 0 80 28" fill="none">
              <path d="M76 14H4" stroke="var(--cream)" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M16 4L4 14L16 24" stroke="var(--cream)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            onClick={scrollNext}
            aria-label="Next"
            className="opacity-50 hover:opacity-100 transition-opacity"
            style={{ display: "flex", background: "none", border: "none", cursor: "pointer", padding: "8px 0", minHeight: 44 }}
          >
            <svg className="w-12 h-7 lg:w-20 lg:h-7" viewBox="0 0 80 28" fill="none">
              <path d="M4 14H76" stroke="var(--cream)" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M64 4L76 14L64 24" stroke="var(--cream)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Carousel — left-aligned with content, bleeds right */}
      <div
        style={{
          paddingLeft: "max(24px, calc((100vw - 1280px) / 2 + 24px))",
          paddingBottom: "var(--space-section-bottom)",
        }}
      >
        <div ref={emblaRef} style={{ overflow: "hidden" }}>
          <div className="flex gap-4 lg:gap-7">
            {studies.map((study, i) => (
              <CaseStudyCard key={study.id} study={study} index={i} />
            ))}

            {/* "View All" end card */}
            <div className="w-[200px] lg:w-[300px]" style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Link href="/case-studies">
                <span className="group flex items-center gap-3 type-label cursor-pointer" style={{ color: "var(--cream)" }}>
                  View All
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
